import React from 'react'
import { Box, Typography, Stack, Button, Paper, TextField, Chip, useMediaQuery, useTheme } from '@mui/material'
import { Speed, Security, HeadsetMic, TrendingFlat, Phone } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import heroBg from '../assets/hero-bg.webp'
import { useContactDetails, toTelHref } from '../hooks/useContactDetails'

const Hero: React.FC = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useLanguage()
  const { phone } = useContactDetails()
  const [postcodeInput, setPostcodeInput] = React.useState('')

  const handleGetStartedClick = () => {
    const trimmed = postcodeInput.trim()
    navigate(trimmed ? `/get-started?postcode=${encodeURIComponent(trimmed)}` : '/get-started')
  }

  const handlePackagesClick = () => {
    navigate('/packages')
  }

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        zIndex: 2,
        bgcolor: '#E7F2F2',
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        borderBottomLeftRadius: { xs: 40, md: 90 },
        borderBottomRightRadius: { xs: 40, md: 90 },
        overflow: 'hidden',
      }}
    >
      {/* Background image */}
      <Box
        component="img"
        src={heroBg}
        alt="Mountain and lake landscape in the Brecon Beacons, Wales"
        {...{ fetchpriority: 'high' }}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
        }}
      />

      {/* Overlay for better text readability */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(231, 242, 242, 0.62) 0%, rgba(231, 242, 242, 0.45) 20%)',
          zIndex: 1,
        }}
      />
      <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 1200, mx: 'auto', width: '100%', px: { xs: 2, md: 4 }, py: { xs: 4, md: 0 }, display: 'grid', gridTemplateColumns: { md: '1.3fr 1fr' }, gap: { xs: 4, md: 6 }, alignItems: 'center' }}>
        <Box>
          <Stack spacing={2}>
            <Chip
              label={t('hero.tagline')}
              variant="outlined"
              sx={{
                width: 'fit-content',
                borderRadius: 999,
                px: 1.5,
                py: 0.5,
                borderColor: '#1565C0',
                color: '#1565C0',
                bgcolor: '#E7F2F2',
                fontWeight: 500,
                fontSize: '0.875rem'
              }}
            />
            <Typography component="h1" sx={{
              fontSize: { xs: 42, md: 72},
              fontWeight: 800,
              lineHeight: 1.1,
              color: '#0A1929',
              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
              letterSpacing: '-0.03em',
              mb: 1
            }}>
                {t('hero.title')}
            </Typography>
            <Typography sx={{
              color: '#ffffff',
              maxWidth: 640,
              fontSize: { xs: 18, md: 20 },
              lineHeight: 1.6,
              fontWeight: 400,
              mb: 0.5,
              textShadow: '0 1px 8px rgba(0,0,0,0.55)',
            }}>
                {t('hero.subtitle')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStartedClick}
                fullWidth={isMobile}
                sx={{
                  minHeight: { xs: 44, md: 'auto' },
                  textTransform: 'none',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  borderRadius: 999,
                  transition: 'transform 150ms ease, box-shadow 150ms ease',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  '&:hover': {
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: '0 10px 24px rgba(0,0,0,0.28)'
                  },
                  '&:active': { transform: 'scale(0.98)' }
                }}
              >
                Check availability in your area
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handlePackagesClick}
                fullWidth={isMobile}
                sx={{
                  minHeight: { xs: 44, md: 'auto' },
                  textTransform: 'none',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  borderRadius: 999,
                  transition: 'transform 150ms ease, box-shadow 150ms ease',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  '&:hover': {
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: '0 10px 24px rgba(0,0,0,0.28)'
                  },
                  '&:active': { transform: 'scale(0.98)' }
                }}
              >
                {t('hero.viewPackages')}
              </Button>
            </Stack>
            <Box sx={{ pt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, auto)' }, gap: { xs: 1.5, md: 3 }, bgcolor: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(6px)', borderRadius: 3, px: 2, py: 1.5 }}>
              {[
                { icon: <Speed sx={{ fontSize: { xs: 16, md: 20 } }} />, label: t('hero.speed'), sub: null, color: '#1976d2', bg: '#008F331A' },
                { icon: <Security sx={{ fontSize: { xs: 16, md: 20 } }} />, label: t('hero.noThrottling'), sub: t('hero.alwaysFast'), color: '#1976d2', bg: 'rgba(25,118,210,0.1)' },
                { icon: <HeadsetMic sx={{ fontSize: { xs: 16, md: 20 } }} />, label: t('hero.support'), sub: t('hero.expertHelp'), color: '#1976d2', bg: 'rgba(25,118,210,0.1)' },
                { icon: <TrendingFlat sx={{ fontSize: { xs: 16, md: 20 } }} />, label: t('hero.noRises'), sub: null, color: '#008F33', bg: 'rgba(0,143,51,0.1)' },
              ].map((stat, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: { xs: 28, md: 36 }, height: { xs: 28, md: 36 }, borderRadius: 2, bgcolor: stat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: stat.color }}>
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A1929', fontSize: { xs: '0.7rem', md: '0.875rem' }, lineHeight: 1.2 }}>{stat.label}</Typography>
                    {stat.sub && <Typography variant="caption" sx={{ color: '#6B7280', fontSize: { xs: '0.6rem', md: '0.75rem' } }}>{stat.sub}</Typography>}
                  </Box>
                </Box>
              ))}
            </Box>
          </Stack>
        </Box>
        <Paper elevation={10} sx={{ 
          p: { xs: 2.5, md: 4 }, 
          borderRadius: 4, 
          bgcolor: 'white', 
          boxShadow: '0 24px 80px rgba(0,0,0,0.12), 0 8px 32px rgba(0,0,0,0.08)',
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.04)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '5px',
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
          }
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: { xs: 0.5, md: 0.5 }, color: '#0A1929', fontSize: { xs: '1rem', md: '1.25rem' } }}>{t('hero.checkAvailability')}</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: { xs: 2, md: 3 }, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>{t('hero.enterPostcode')}</Typography>
          <Stack spacing={{ xs: 1.5, md: 2 }}>
            <TextField
              fullWidth
              placeholder={t('hero.postcodeLabel')}
              size="medium"
              value={postcodeInput}
              onChange={e => setPostcodeInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGetStartedClick()}
              inputProps={{ 'aria-label': 'Enter postcode to check service availability', maxLength: 8 }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  transition: 'all 0.2s ease',
                  '&:hover fieldset': { borderColor: '#1976d2' },
                  '&.Mui-focused fieldset': { borderColor: '#1976d2', borderWidth: 2 },
                }
              }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, md: 2 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth={isMobile}
                onClick={handleGetStartedClick}
                sx={{
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  textTransform: 'none',
                  // Ensure minimum 44px height for touch target
                  minHeight: { xs: 44, md: 'auto' },
                  py: { xs: 1.25, md: 1.5 },
                  transition: 'transform 150ms ease, box-shadow 150ms ease',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  '&:hover': {
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: '0 10px 24px rgba(0,0,0,0.28)'
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                  }
                }}
              >
                Check availability
              </Button>
              <Button
                variant="text"
                size="large"
                onClick={handlePackagesClick}
                fullWidth={isMobile}
                sx={{
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  // Ensure minimum 44px height for touch target
                  minHeight: { xs: 44, md: 'auto' },
                  py: { xs: 1.25, md: 1.5 },
                  transition: 'transform 150ms ease',
                  '&:hover': {
                    transform: 'translateY(-2px) scale(1.02)'
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                  }
                }}
              >
                {t('hero.seePlans')}
              </Button>
            </Stack>
            <Box component="a" href={toTelHref(phone)} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#1976d2', textDecoration: 'none', fontSize: { xs: '0.8rem', md: '0.9rem' }, fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
              <Phone sx={{ fontSize: 16 }} />
              {phone}
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Box>
  )
}

export default Hero
